import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import type Company from '../../../../models/company.js';
import type { LedgerBalanceReading } from '../../../../models/company.js';
import definition from '@job-schemas/public-ledger/fund-test-company.json';
import { jobSchema } from '../../job-schema.js';
import { createLogger } from '../../../../ns-services/logger.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { isLedgerNetwork, ledgerNetworkLabel } from '../../../../helpers/public-ledger-network.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import { confirmTestFunding } from '../../../../helpers/public-ledger-credits-api.js';
import { fundTestCompanyFigures, validateFundedCredits } from '../../../../helpers/credit-pricing.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import '../../../../ns-elements/ns-credits-input.js';
import '../../../../ns-elements/ns-ledger-balance.js';

const logger = createLogger('ns-public-ledger-fund-test-company');

/**
 * The PUBLIC_LEDGER team's urgent card for a MAINNET company outside production, which can't buy credits (item 2). The team
 * sends funds to the company's public ledger account by hand, outside nodeStream, then enters the amount;
 * public-ledger-credits-confirm-test-funding reads the account fresh and completes the card. The team isn't a member of
 * the company, so the balance shown is the reading the lambda recorded, not a read-balance call.
 */
@customElement(NSPublicLedgerFundTestCompany.is)
export default class NSPublicLedgerFundTestCompany extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-fund-test-company';
  }

  static get schema(): JobSchema {
    // Completed by the confirm lambda, never by an outcome button
    return jobSchema(definition, { outcomes: { funded: { type: 'hidden' } }, icon: 'transaction' });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        margin: 0 0 var(--wa-space-m);
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __company?: Company;

  @state()
  private __credits: number | null = null;

  @state()
  private __inputError = '';

  @state()
  private __busy = false;

  async connectedCallback() {
    await super.connectedCallback();
    const { companyId } = fundTestCompanyFigures(this._job?.metadata);
    if (!companyId || !this._db) return;
    try {
      this.__company = await this._db.getById<Company>('company', companyId);
    } catch (err) {
      logger.warn('Unable to load the company', err);
    }
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __confirm() {
    const job = this._job;
    if (!job || !this._auth?.token || this.__busy) return;
    this.__inputError = validateFundedCredits(this.__credits) ?? '';
    if (this.__inputError || this.__credits === null) return;
    this.__busy = true;
    this.clearError();
    this.loading('Checking the public ledger');
    try {
      await confirmTestFunding(this._auth.token, { jobId: job.id, fundedCredits: this.__credits });
      const updated = await this._db?.getById<Job>('job', job.id);
      if (updated) this._job = updated;
      this.loaded();
      if (updated?.status === 'COMPLETE') this._jobComplete();
      this.requestUpdate();
    } catch (err) {
      this.loaded();
      this.showError(lambdaRefusal(err, "The funding couldn't be confirmed. Please try again."));
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const figures = fundTestCompanyFigures(this._job?.metadata);
    const network = isLedgerNetwork(figures.network) ? figures.network : null;
    const done = this._job?.status === 'COMPLETE';
    // The account with its copy control and explorer link; once funded, the balance the lambda recorded
    // (BALANCE_AFTER_CREDITS is in credits, a reading in tinybars: credits × 10^6)
    const reading: LedgerBalanceReading = {
      accountId: figures.accountId,
      network,
      tinybars:
        done && figures.balanceAfterCredits !== null
          ? String(Math.round(figures.balanceAfterCredits * 1_000_000))
          : null,
      readAt: null,
    };
    return html`
      <ns-job-layout>
        <p slot="intro">
          This test company's public ledger account is on ${network ? ledgerNetworkLabel(network) : 'mainnet'}, where it
          can't buy nodeStream credits outside production. Fund it by hand instead.
        </p>
        <div class="job-panel">
          <h3>The company's account</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: this.__company?.name ?? undefined },
              { label: 'Network', value: network ? ledgerNetworkLabel(network) : (figures.network ?? undefined) },
            ]}
          ></ns-detail-list>
          <ns-ledger-balance
            label=${done ? 'Balance after funding' : 'Company account'}
            empty-text=${done ? 'Not read' : 'Read once you have funded it'}
            .reading=${reading}
            copyable
          ></ns-ledger-balance>
        </div>
        ${done
          ? nothing
          : html`
              <div class="job-panel">
                <h3>Enter the amount sent</h3>
                <p>
                  Send funds to this account by hand, outside nodeStream, then enter the amount in credits (100 credits
                  are 1 unit of the public ledger's own currency).
                </p>
                <ns-credits-input
                  required
                  label="Amount sent (credits)"
                  .credits=${this.__credits}
                  .error=${this.__inputError}
                  @credits-change=${(e: CustomEvent<{ credits: number | null }>) => {
                    this.__credits = e.detail.credits;
                    this.__inputError = '';
                  }}
                ></ns-credits-input>
              </div>
            `}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__confirm()}>
        I've funded it
      </wa-button>
    `;
  }
}
